// app_core/src/map/area-selector-map.js
// Handles area selection from map markers (UNIFIED mode only)
// Reuses logic from area-selector/index.js for consistency

import { LOGGING, MAP_DATA_STRATEGY, MAP_SETTINGS } from '/settings.js';
import { updateUrl } from '../utils/url-utils.js';
import { updateAreaMarkers } from './area-markers.js';

/**
 * Initializes area marker click handlers for area selection.
 * When a marker is clicked, it triggers area selection with all associated updates.
 *
 * @param {Array} areas - Array of area objects from API
 * @param {L.Map} mapInstance - Leaflet map instance
 * @param {Function} onAreaSelected - Callback after area is selected (optional)
 */
export async function initializeAreaMarkerSelection(areas, mapInstance, onAreaSelected = null) {
    // Only proceed if enabled and in UNIFIED mode
    if (!MAP_DATA_STRATEGY.SELECT_AREA_FROM_MAP || MAP_DATA_STRATEGY.TYPE !== 'UNIFIED') {
        return;
    }

    // Get all area markers from the map
    const markerLayers = mapInstance.eachLayer((layer) => {
        // Check if this is an area marker (has areaData property)
        if (layer.areaData) {
            attachAreaMarkerClickHandler(layer, areas, mapInstance, onAreaSelected);
        }
    });

    if (LOGGING.ENABLED && LOGGING.DEBUG) {
        console.log('[Area Selector Map] Initialized area marker click handlers');
    }
}

/**
 * Attaches click handler to a single area marker.
 * @private
 */
function attachAreaMarkerClickHandler(marker, areas, mapInstance, onAreaSelected) {
    marker.on('click', async () => {
        const area = marker.areaData;
        if (area) {
            await selectAreaFromMarker(area, areas, mapInstance, onAreaSelected);
        }
    });
}

/**
 * Selects an area when its map marker is clicked.
 * Reuses the same logic as area-selector/index.js for consistency.
 *
 * @param {Object} area - Area object to select
 * @param {Array} areas - All available areas (for updating markers)
 * @param {L.Map} mapInstance - Leaflet map instance
 * @param {Function} onAreaSelected - Callback after selection
 */
async function selectAreaFromMarker(area, areas, mapInstance, onAreaSelected) {
    if (LOGGING.ENABLED) {
        console.log('[Area Selector Map] Selected area from marker:', area.slug);
    }

    // 1. Update URL
    updateUrl(area.slug, null);

    // 2. Update localStorage
    try {
        localStorage.setItem('selected_area_slug', area.slug);
    } catch (e) {
        console.warn('Failed to save area selection to localStorage', e);
    }

    // 3. Dispatch areaChanged event for favorites and other listeners
    window.dispatchEvent(new CustomEvent('areaChanged', { detail: { area } }));

    // 4. Load map data and update styles (same as area-selector)
    const { loadGeoJSONMap, updateMapStyles } = await import('./map.js');

    // For PER_AREA, load new map data (though we're UNIFIED, this is for safety)
    if (MAP_DATA_STRATEGY.TYPE === 'PER_AREA') {
        if (LOGGING.ENABLED) {
            console.log(`[Area Selector Map] Loading map data for area: ${area.slug}`);
        }
        await loadGeoJSONMap(mapInstance, area.slug);
    }

    // For UNIFIED, update map styles to highlight selected area
    if (MAP_DATA_STRATEGY.TYPE === 'UNIFIED') {
        if (LOGGING.ENABLED) {
            console.log(`[Area Selector Map] Updating map styles for area: ${area.slug}`);
        }
        updateMapStyles(area.slug);
    }

    // 5. Clear all spots and center on selected area
    const { clearAllSpots } = await import('./map-visibility.js');
    clearAllSpots();

    // Center map on selected area with zoom
    if (area.center_lat && area.center_lng) {
        mapInstance.setView(
            [area.center_lat, area.center_lng],
            area.zoom || MAP_SETTINGS.DEFAULT_ZOOM,
            { animate: true }
        );
    }

    // 6. Apply max bounds if PER_AREA mode
    if (MAP_DATA_STRATEGY.TYPE === 'PER_AREA') {
        if (area.bounds_north && area.bounds_south && area.bounds_east && area.bounds_west) {
            const bounds = [
                [area.bounds_south, area.bounds_west],
                [area.bounds_north, area.bounds_east]
            ];
            mapInstance.setMaxBounds(bounds);

            if (LOGGING.ENABLED && LOGGING.DEBUG) {
                console.log(`[Area Selector Map] Applied max bounds for ${area.slug}:`, bounds);
            }
        }
    }

    // 7. Update area markers to reflect new selection
    updateAreaMarkers(areas, area.slug, mapInstance);

    // 8. Trigger callback to update headers/UI
    if (onAreaSelected) {
        onAreaSelected(area);
    }

    if (LOGGING.ENABLED) {
        console.log('[Area Selector Map] Area selection complete:', area.slug);
    }
}

/**
 * Refreshes area markers when area list changes.
 * @param {Array} areas - Updated list of areas
 * @param {string} selectedAreaSlug - Currently selected area slug
 * @param {L.Map} mapInstance - Leaflet map instance
 */
export function refreshAreaMarkers(areas, selectedAreaSlug, mapInstance) {
    updateAreaMarkers(areas, selectedAreaSlug, mapInstance);
}
